import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/db/client";
import { useInAppEnabled } from "@/hooks/useNotificationPreferences";

export interface Mention {
  id: string;
  comment_id: string;
  mentioned_user_id: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  comment: {
    id: string;
    comment_text: string;
    backlog_item_id: string;
    user_id: string;
    created_at: string;
    user: {
      id: string;
      first_name: string | null;
      last_name: string | null;
      email: string;
      avatar_url: string | null;
    } | null;
    backlog_item: {
      id: string;
      task: string;
      ticket_number: number;
      deleted_at: string | null;
    } | null;
  } | null;
}

export const useMentions = () => {
  // Hidden, not deleted, when someone turns in-app mentions off (#9112): the
  // rows stay, because the email outbox reads them.
  const inApp = useInAppEnabled("mentioned");
  return useQuery({
    queryKey: ["mentions"],
    select: (mentions) => (inApp ? mentions : []),
    queryFn: async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return [];

        // Fetch mentions with comments and backlog items
        // Note: comments.user_id FK points to auth.users, not profiles,
        // so we cannot join profiles via PostgREST — resolve user data separately.
        const { data: mentions, error } = await supabase
          .from("comment_mentions")
          .select(
            `
            id,
            comment_id,
            mentioned_user_id,
            is_read,
            read_at,
            created_at,
            comment:comments(
              id,
              comment_text,
              backlog_item_id,
              user_id,
              created_at,
              backlog_item:backlog_items!backlog_item_id(
                id,
                task,
                ticket_number,
                deleted_at
              )
            )
          `,
          )
          .eq("mentioned_user_id", user.id)
          .order("created_at", { ascending: false });

        if (error) throw error;

        // Collect unique user IDs from comments to fetch profiles
        const userIds = [...new Set((mentions || []).map((m) => (m as any).comment?.user_id).filter(Boolean))];

        let profilesMap: Record<
          string,
          { id: string; first_name: string | null; last_name: string | null; email: string; avatar_url: string | null }
        > = {};
        if (userIds.length > 0) {
          const { data: profiles } = await supabase
            .from("profiles")
            .select("id, first_name, last_name, email, avatar_url")
            .in("id", userIds);
          if (profiles) {
            profilesMap = Object.fromEntries(profiles.map((p) => [p.id, p]));
          }
        }

        // Attach user profiles to comments
        const enriched = ((mentions || []) as unknown as Mention[]).map((m) => {
          if (m.comment) {
            m.comment.user = profilesMap[m.comment.user_id] || null;
          }
          return m;
        });

        // Filter out mentions where backlog item is deleted
        return enriched.filter(
          (m) => m.comment && (!m.comment.backlog_item || m.comment.backlog_item.deleted_at === null),
        );
      } catch (error) {
        console.error("Error fetching mentions:", error);
        return [];
      }
    },
    retry: false,
    staleTime: 60 * 1000, // 1 minute — realtime sync handles freshness
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
};

export const useUnreadMentionsCount = () => {
  const inApp = useInAppEnabled("mentioned");
  return useQuery({
    queryKey: ["unread-mentions-count"],
    select: (count) => (inApp ? count : 0),
    queryFn: async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return 0;

        // Fetch unread mentions
        const { data: mentions, error } = await supabase
          .from("comment_mentions")
          .select("comment_id")
          .eq("mentioned_user_id", user.id)
          .eq("is_read", false);

        if (error) throw error;
        if (!mentions || mentions.length === 0) return 0;

        // Get comment IDs and their backlog items to filter out deleted ones
        const commentIds = mentions.map((m) => m.comment_id);
        const { data: comments } = await supabase.from("comments").select("id, backlog_item_id").in("id", commentIds);

        if (!comments || comments.length === 0) return 0;

        // Get backlog items to check deleted_at
        const backlogItemIds = comments.map((c) => c.backlog_item_id);
        const { data: backlogItems } = await supabase
          .from("backlog_items")
          .select("id, deleted_at")
          .in("id", backlogItemIds);

        if (!backlogItems) return 0;

        // Create a map of backlog item IDs to their deleted status
        const deletedItemIds = new Set(backlogItems.filter((item) => item.deleted_at !== null).map((item) => item.id));

        // Count mentions where the backlog item is not deleted
        const validCount = comments.filter((comment) => !deletedItemIds.has(comment.backlog_item_id)).length;

        return validCount;
      } catch (error) {
        console.error("Error fetching unread count:", error);
        return 0;
      }
    },
    retry: false,
    staleTime: 60 * 1000, // Cache for 60 seconds
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    placeholderData: (previousData) => previousData ?? 0, // Keep showing old data
  });
};

export const useMarkMentionAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mentionId: string) => {
      const { error } = await supabase
        .from("comment_mentions")
        .update({
          is_read: true,
          read_at: new Date().toISOString(),
        })
        .eq("id", mentionId);

      if (error) throw error;
    },
    onMutate: async (mentionId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["mentions"] });
      await queryClient.cancelQueries({ queryKey: ["unread-mentions-count"] });

      // Snapshot previous values
      const previousMentions = queryClient.getQueryData(["mentions"]);
      const previousCount = queryClient.getQueryData(["unread-mentions-count"]);

      // Optimistically update mentions
      queryClient.setQueryData<Mention[]>(["mentions"], (old) => {
        if (!old) return old;
        return old.map((mention) =>
          mention.id === mentionId ? { ...mention, is_read: true, read_at: new Date().toISOString() } : mention,
        );
      });

      // Optimistically update count
      queryClient.setQueryData<number>(["unread-mentions-count"], (old) => {
        return Math.max(0, (old || 0) - 1);
      });

      return { previousMentions, previousCount };
    },
    onError: (err, mentionId, context) => {
      // Rollback on error
      if (context?.previousMentions) {
        queryClient.setQueryData(["mentions"], context.previousMentions);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(["unread-mentions-count"], context.previousCount);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["mentions"] });
      queryClient.invalidateQueries({ queryKey: ["unread-mentions-count"] });
    },
  });
};

export const useMarkAllMentionsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("comment_mentions")
        .update({
          is_read: true,
          read_at: new Date().toISOString(),
        })
        .eq("mentioned_user_id", user.id)
        .eq("is_read", false);

      if (error) throw error;
    },
    onMutate: async () => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["mentions"] });
      await queryClient.cancelQueries({ queryKey: ["unread-mentions-count"] });

      // Snapshot previous values
      const previousMentions = queryClient.getQueryData(["mentions"]);
      const previousCount = queryClient.getQueryData(["unread-mentions-count"]);

      // Optimistically update all mentions to read
      queryClient.setQueryData<Mention[]>(["mentions"], (old) => {
        if (!old) return old;
        return old.map((mention) => ({
          ...mention,
          is_read: true,
          read_at: new Date().toISOString(),
        }));
      });

      // Optimistically update count to 0
      queryClient.setQueryData<number>(["unread-mentions-count"], 0);

      return { previousMentions, previousCount };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousMentions) {
        queryClient.setQueryData(["mentions"], context.previousMentions);
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(["unread-mentions-count"], context.previousCount);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["mentions"] });
      queryClient.invalidateQueries({ queryKey: ["unread-mentions-count"] });
    },
  });
};
